import { errorMessage } from '../lib/errors';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useApi } from '../services';
import { useSession } from '../state/session';
import { IconClose } from './icons';

interface Msg {
  role: 'user' | 'bot';
  text: string;
  facts?: Record<string, unknown>[];
  error?: boolean;
}

const SUGGESTIONS = ['Why is Wednesday so low?', 'How busy will Saturday be?', 'How many staff do I need this week?', 'How accurate have forecasts been?'];

function describeFact(f: Record<string, unknown>): string {
  const tool = String(f.tool ?? 'data');
  const rest = Object.entries(f).filter(([k]) => k !== 'tool').map(([k, v]) => `${k.replace(/_/g, ' ')} ${String(v)}`);
  return [tool.replace(/^get_/, '').replace(/_/g, ' '), ...rest].join(' · ');
}

export function AssistantPanel({ onClose }: { onClose: () => void }) {
  const api = useApi();
  const { siteId, site } = useSession();
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  const chat = useMutation({
    mutationFn: (message: string) => api.chat(siteId, message),
    onSuccess: (r) => setMsgs((m) => [...m, { role: 'bot', text: r.reply, facts: r.facts }]),
    onError: (e) => setMsgs((m) => [...m, { role: 'bot', text: errorMessage(e), error: true }]),
  });

  useEffect(() => {
    inputRef.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight });
  }, [msgs, chat.isPending]);

  const send = (text: string) => {
    const t = text.trim();
    if (!t || chat.isPending) return;
    setMsgs((m) => [...m, { role: 'user', text: t }]);
    setDraft('');
    chat.mutate(t);
  };
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    send(draft);
  };

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <aside className="drawer" role="dialog" aria-modal="true" aria-labelledby="assistant-title">
        <div className="drawer-head">
          <div>
            <h2 id="assistant-title">Ask DemandPilot</h2>
            <div className="muted small">About {site.name}</div>
          </div>
          <button type="button" className="btn btn-ghost btn-icon" aria-label="Close assistant" onClick={onClose}>
            <IconClose />
          </button>
        </div>
        <div className="drawer-body" ref={bodyRef} aria-live="polite">
          {msgs.length === 0 && (
            <div>
              <p className="muted small">Ask about any day’s forecast, prep, rostering or accuracy. Answers use this site’s forecast data.</p>
              <div style={{ display: 'grid', gap: 6 }}>
                {SUGGESTIONS.map((s) => (
                  <button key={s} type="button" className="btn btn-sm" style={{ justifyContent: 'flex-start' }} onClick={() => send(s)}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {msgs.map((m, i) => (
            <div key={i} className={`msg ${m.role === 'user' ? 'msg-user' : 'msg-bot'}`} style={m.error ? { color: 'var(--crit)' } : undefined}>
              {m.text}
              {m.facts && m.facts.length > 0 && (
                <div className="msg-facts">Based on: {m.facts.map(describeFact).join('; ')}</div>
              )}
            </div>
          ))}
          {chat.isPending && <div className="msg msg-bot muted" aria-label="Thinking">Thinking…</div>}
        </div>
        <form className="drawer-foot" onSubmit={onSubmit}>
          <label htmlFor="assistant-input" className="sr-only">Message</label>
          <input id="assistant-input" ref={inputRef} type="text" value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Why is Wednesday so low?" autoComplete="off" />
          <button type="submit" className="btn btn-primary" disabled={!draft.trim() || chat.isPending}>Send</button>
        </form>
      </aside>
    </>
  );
}
